/** @jsxImportSource @emotion/react */
import PropTypes from "prop-types";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import t from "../../../theme";

const introParagraphs = [
  "This FAQ describes how your personal information is collected, used, and shared when you visit or make a purchase from https://money-mails.com (the Site).",
  "Similar to other commercial websites, our website uses cookies and server logs to understand visits, page views, time spent, and related usage signals.",
];

const faqItems = [
  {
    question: "What does this policy cover?",
    answer:
      "This policy sets out the conditions under which we may process any information that we collect from you, or that you provide to us. We take seriously the protection of your privacy and confidentiality and do not intend your personal data to be used for purposes unintended by you.",
  },
  {
    question: "What personal information do you collect?",
    answer:
      "Money-Mails Inc may collect personally identifiable information when you purchase any product on https://money-mails.com that may be subject to delivery to your designated address. This may include name, address, e-mail address, phone number, and certain anonymous demographic information that is not unique to you.",
  },
  {
    question: "What information do you collect and why?",
    answer:
      "We may collect information about you directly from you, as well as automatically through your use of our site or services. Certain areas and features of our site may require product purchase details such as name, email address, delivery address, payment details, and IP address. We may also collect device ID, location, language information, and general analytics about how you use the site.",
  },
  {
    question: "Do you share my information with third parties?",
    answer:
      "We do not sell, rent, or lease customer lists to third parties. Trusted partners may process information only as needed to support services like analytics, communications, customer support, or deliveries, and they must preserve confidentiality.",
  },
];

const styles = {
  wrapper: {
    display: "flex",
    flexDirection: "column",
    fontFamily: t.font.family,
    color: t.text.primary,
  },
  heading: {
    margin: 0,
    fontSize: "1.75rem",
    fontWeight: 700,
    color: t.text.primary,
  },
  subheading: {
    margin: "8px 0 0",
    fontSize: "0.9rem",
    color: t.text.muted,
  },
  intro: {
    margin: "20px 0 0",
    fontSize: "0.95rem",
    lineHeight: 1.7,
    color: t.text.secondary,
  },
  accordionGroup: {
    marginTop: "24px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  accordion: {
    background: t.glass.light,
    borderRadius: `${t.radius.md} !important`,
    border: t.border.light,
    boxShadow: "none",
    "&:before": { display: "none" },
    "&.Mui-expanded": {
      margin: 0,
      borderColor: t.green.borderSelected,
    },
  },
  summary: {
    fontWeight: 600,
    fontSize: "0.95rem",
    color: t.text.primary,
  },
  details: {
    fontSize: "0.9rem",
    lineHeight: 1.7,
    color: t.text.secondary,
    paddingTop: 0,
  },
};

const FAQContent = ({ title = "FAQ" }) => (
  <div css={styles.wrapper}>
    <h3 css={styles.heading}>{title}</h3>
    <p css={styles.subheading}>
      Helpful information about privacy, data usage, and how Money-Mails
      handles personal information.
    </p>

    {introParagraphs.map((paragraph) => (
      <p key={paragraph} css={styles.intro}>
        {paragraph}
      </p>
    ))}

    <div css={styles.accordionGroup}>
      {faqItems.map((item) => (
        <Accordion key={item.question} disableGutters css={styles.accordion}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: t.green.solid }} />}
          >
            <span css={styles.summary}>{item.question}</span>
          </AccordionSummary>
          <AccordionDetails css={styles.details}>
            {item.answer}
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  </div>
);

FAQContent.propTypes = {
  title: PropTypes.string,
};

export default FAQContent;
